// ============================================
// RouteMapPicker — Reusable Map Component
// Places Autocomplete + Directions + Route Highlight
// Stabilized to prevent unnecessary re-renders
// ============================================

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
    GoogleMap,
    DirectionsRenderer,
    Marker,
    Autocomplete,
} from '@react-google-maps/api';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { useTheme } from '../../context/ThemeContext';
import { getPremiumMapOptions } from '../../utils/mapStyles';

const DEFAULT_ON_CHANGE = () => { };
const DEFAULT_CENTER = { lat: 51.5074, lng: -0.1278 }; // London, UK

// MAP_OPTIONS is now dynamic based on theme

// ── Theme colour maps ──────────────────────────────
const THEMES = {
    indigo: { polyline: '#4f46e5', markerFrom: '#4f46e5', markerTo: '#dc2626', accent: '#4f46e5', ring: 'focus:ring-indigo-500', border: 'focus:border-indigo-500', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', btn: 'bg-indigo-600 hover:bg-indigo-700' },
    teal: { polyline: '#0f766e', markerFrom: '#0f766e', markerTo: '#dc2626', accent: '#0f766e', ring: 'focus:ring-teal-500', border: 'focus:border-teal-500', badge: 'bg-teal-50 text-teal-700 border-teal-200', btn: 'bg-teal-600 hover:bg-teal-700' },
    amber: { polyline: '#d97706', markerFrom: '#d97706', markerTo: '#dc2626', accent: '#d97706', ring: 'focus:ring-amber-500', border: 'focus:border-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200', btn: 'bg-amber-600 hover:bg-amber-700' },
    emerald: { polyline: '#059669', markerFrom: '#059669', markerTo: '#dc2626', accent: '#059669', ring: 'focus:ring-emerald-500', border: 'focus:border-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    rose: { polyline: '#e11d48', markerFrom: '#e11d48', markerTo: '#e11d48', accent: '#e11d48', ring: 'focus:ring-rose-500', border: 'focus:border-rose-500', badge: 'bg-rose-50 text-rose-700 border-rose-200', btn: 'bg-rose-600 hover:bg-rose-700' },
};

/**
 * RouteMapPicker
 *
 * Props:
 * @param {string}   fromAddress   - Controlled value for origin (pre-fill)
 * @param {string}   toAddress     - Controlled value for destination (pre-fill)
 * @param {function} onChange      - Called with { fromAddress, fromCoords, toAddress, toCoords, distanceText, durationText }
 * @param {string}   height        - CSS height of the map container (default '320px')
 * @param {string}   theme         - 'indigo' | 'teal' | 'amber'  (default 'indigo')
 * @param {boolean}  readOnly      - If true, hides the search inputs; just shows the route
 * @param {object}   fromCoords    - { lat, lng } pre-set origin coords (used in readOnly mode)
 * @param {object}   toCoords      - { lat, lng } pre-set destination coords (used in readOnly mode)
 */
const RouteMapPicker = React.memo(({
    fromAddress: initialFrom = '',
    toAddress: initialTo = '',
    fromCoords: initialFromCoords = null,
    toCoords: initialToCoords = null,
    onChange = DEFAULT_ON_CHANGE,
    height = '320px',
    theme = 'indigo',
    readOnly = false,
    singleSelect = false,
    singleSelectTo = false,
    fromPlaceholder = 'From — Enter pickup / start address',
    toPlaceholder = 'To — Enter dropoff / destination address',
}) => {
    const colours = THEMES[theme] || THEMES.indigo;

    const { isLoaded, loadError } = useGoogleMaps();
    const { theme: globalTheme } = useTheme();
    const isDark = globalTheme === 'dark';
    const mapOptions = useMemo(() => getPremiumMapOptions(isDark), [isDark]);

    // ── Local state ─────────────────────────────────
    const [fromAddress, setFromAddress] = useState(initialFrom);
    const [toAddress, setToAddress] = useState(initialTo);
    const [fromCoords, setFromCoords] = useState(initialFromCoords);
    const [toCoords, setToCoords] = useState(initialToCoords);
    const [directions, setDirections] = useState(null);
    const [routeInfo, setRouteInfo] = useState(null);   // { distance, duration }
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState('');

    const fromBoxRef = useRef(null);
    const toBoxRef = useRef(null);
    const lastFetchKey = useRef('');
    const mapRef = useRef(null);         // Use ref instead of state for map instance
    const directionsFetched = useRef(false); // Track if we already fetched directions

    // Refs to hold latest values for use in callbacks (avoids re-creating callbacks)
    const fromAddressRef = useRef(initialFrom);
    const toAddressRef = useRef(initialTo);
    const onChangeRef = useRef(onChange);

    // Keep refs in sync
    fromAddressRef.current = fromAddress;
    toAddressRef.current = toAddress;
    onChangeRef.current = onChange;

    // Refs to track prop changes
    const prevInitialFrom = useRef(initialFrom);
    const prevInitialTo = useRef(initialTo);
    const prevInitialFromCoords = useRef(initialFromCoords);
    const prevInitialToCoords = useRef(initialToCoords);

    // ── Stable map container style ──
    const mapContainerStyle = useMemo(() => ({
        width: '100%',
        height,
    }), [height]);

    // ── Sync local state only when PROPS actually change ──
    useEffect(() => {
        if (initialFrom !== prevInitialFrom.current) {
            setFromAddress(initialFrom);
            prevInitialFrom.current = initialFrom;
        }
    }, [initialFrom]);

    useEffect(() => {
        if (initialTo !== prevInitialTo.current) {
            setToAddress(initialTo);
            prevInitialTo.current = initialTo;
        }
    }, [initialTo]);

    useEffect(() => {
        const cur = JSON.stringify(initialFromCoords);
        const prev = JSON.stringify(prevInitialFromCoords.current);
        if (cur !== prev) {
            setFromCoords(initialFromCoords);
            prevInitialFromCoords.current = initialFromCoords;
            directionsFetched.current = false; // Reset so we re-fetch with new coords
        }
    }, [initialFromCoords]);

    useEffect(() => {
        const cur = JSON.stringify(initialToCoords);
        const prev = JSON.stringify(prevInitialToCoords.current);
        if (cur !== prev) {
            setToCoords(initialToCoords);
            prevInitialToCoords.current = initialToCoords;
            directionsFetched.current = false; // Reset so we re-fetch with new coords
        }
    }, [initialToCoords]);

    // ── Fetch Directions — stable callback (no fromAddress/toAddress/onChange in deps) ──
    const fetchDirections = useCallback((origin, destination) => {
        if (!origin || !destination || !window.google) return;

        const oKey = typeof origin === 'string' ? origin : `${origin.lat},${origin.lng}`;
        const dKey = typeof destination === 'string' ? destination : `${destination.lat},${destination.lng}`;
        const currentKey = `${oKey}|${dKey}`;

        if (lastFetchKey.current === currentKey) return;
        lastFetchKey.current = currentKey;

        setFetching(true);
        setFetchError('');

        const service = new window.google.maps.DirectionsService();
        service.route(
            {
                origin,
                destination,
                travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                setFetching(false);
                if (status === 'OK' && result) {
                    setDirections(result);
                    directionsFetched.current = true;
                    const leg = result.routes[0]?.legs[0];
                    const info = {
                        distance: leg?.distance?.text || '',
                        duration: leg?.duration?.text || '',
                    };
                    setRouteInfo(info);

                    // Use refs to get latest values without causing callback recreation
                    onChangeRef.current({
                        fromAddress: fromAddressRef.current,
                        fromCoords: origin,
                        toAddress: toAddressRef.current,
                        toCoords: destination,
                        distanceText: info.distance,
                        durationText: info.duration,
                    });
                } else {
                    setDirections(null);
                    setRouteInfo(null);
                    if (status !== 'ZERO_RESULTS' && status !== 'INVALID_REQUEST') {
                        setFetchError(`Route error: ${status}`);
                    }
                }
            }
        );
    }, []); // No deps — uses refs for mutable values

    // ── Map center: memoized to prevent reference changes ──
    const mapCenter = useMemo(() => {
        return fromCoords || toCoords || DEFAULT_CENTER;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        fromCoords?.lat, fromCoords?.lng,
        toCoords?.lat, toCoords?.lng
    ]);

    // ── Zoom level: memoized ──
    const zoomLevel = useMemo(() => {
        if (singleSelectTo) return toCoords ? 15 : 11;
        if (!fromCoords) return 11;
        if (singleSelect) return 15;
        return toCoords ? 12 : 14;
    }, [fromCoords, toCoords, singleSelect, singleSelectTo]);

    // ── Trigger directions only when coords change and haven't fetched yet ──
    useEffect(() => {
        if (!isLoaded) return;

        if (singleSelect || singleSelectTo) {
            setDirections(null);
            setRouteInfo(null);
            return;
        }

        // Only fetch if we haven't already fetched for these coords
        if (fromCoords && toCoords) {
            fetchDirections(fromCoords, toCoords);
        } else if (readOnly && fromAddress && toAddress && !directionsFetched.current) {
            fetchDirections(fromAddress, toAddress);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromCoords?.lat, fromCoords?.lng, toCoords?.lat, toCoords?.lng, isLoaded, singleSelect, singleSelectTo]);

    // ── Map load callback — use ref, not state ──
    const onMapLoad = useCallback((mapInstance) => {
        mapRef.current = mapInstance;
    }, []);

    // ── Autocomplete callbacks — stable ──
    const onFromPlaceChanged = useCallback(() => {
        const autocomplete = fromBoxRef.current;
        if (!autocomplete) return;
        const place = autocomplete.getPlace();
        const loc = place.geometry?.location;
        if (!loc) return;
        const coords = { lat: loc.lat(), lng: loc.lng() };
        const addr = place.formatted_address || place.name || '';

        setFromAddress(addr);
        setFromCoords(coords);
        directionsFetched.current = false;

        if (singleSelect || singleSelectTo) {
            onChangeRef.current({
                fromAddress: addr,
                fromCoords: coords,
                toAddress: '',
                toCoords: null
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleSelect]);

    const onToPlaceChanged = useCallback(() => {
        const autocomplete = toBoxRef.current;
        if (!autocomplete) return;
        const place = autocomplete.getPlace();
        const loc = place.geometry?.location;
        if (!loc) return;
        const coords = { lat: loc.lat(), lng: loc.lng() };
        const addr = place.formatted_address || place.name || '';

        setToAddress(addr);
        setToCoords(coords);
        directionsFetched.current = false;

        if (singleSelectTo) {
            onChangeRef.current({
                fromAddress: '',
                fromCoords: null,
                toAddress: addr,
                toCoords: coords
            });
        }
    }, [singleSelectTo]);

    // ── Polyline options: stable ref ──
    const polylineOptions = useMemo(() => ({
        strokeColor: colours.polyline,
        strokeWeight: 5,
        strokeOpacity: 0.85,
    }), [colours.polyline]);

    // ── DirectionsRenderer options: memoized ──
    const directionsRendererOptions = useMemo(() => {
        if (!directions) return null;
        return {
            directions,
            suppressMarkers: true,
            polylineOptions,
        };
    }, [directions, polylineOptions]);

    if (loadError) {
        return (
            <div className="w-full rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-600 font-medium">
                <div className="flex items-center gap-2"><svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Failed to load Google Maps. Check your API key.</div>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="w-full rounded-2xl bg-slate-100 flex items-center justify-center" style={{ height }}>
                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="w-full rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-900">

            {/* ── Address Inputs (hidden in readOnly mode) ── */}
            {!readOnly && (
                <div className="p-4 space-y-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    {/* From (hidden in singleSelectTo mode) */}
                    {!singleSelectTo && (
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black shadow"
                                style={{ backgroundColor: colours.markerFrom }}>A</div>
                            <div className="flex-1">
                                <Autocomplete
                                    onLoad={(ref) => (fromBoxRef.current = ref)}
                                    onPlaceChanged={onFromPlaceChanged}
                                    options={{
                                        componentRestrictions: { country: 'gb' },
                                        fields: ['address_components', 'geometry', 'formatted_address', 'name'],
                                        types: ['establishment', 'geocode']
                                    }}
                                >
                                    <input
                                        type="text"
                                        placeholder={fromPlaceholder}
                                        value={fromAddress}
                                        onChange={(e) => setFromAddress(e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-900 outline-none focus:ring-2 ${colours.ring} ${colours.border} transition-all placeholder:text-slate-400 dark:text-white`}
                                    />
                                </Autocomplete>
                            </div>
                        </div>
                    )}

                    {/* To (shown when not singleSelect; also shown in singleSelectTo mode) */}
                    {(!singleSelect || singleSelectTo) && (
                        <>
                            {/* Connector Line (only if both A and B are visible) */}
                            {!singleSelect && !singleSelectTo && (
                                <div className="ml-4 w-[1px] h-4 bg-slate-300 dark:bg-slate-600 mx-auto" style={{ marginLeft: '19px' }} />
                            )}

                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black shadow"
                                    style={{ backgroundColor: colours.markerTo }}>B</div>
                                <div className="flex-1">
                                    <Autocomplete
                                        onLoad={(ref) => (toBoxRef.current = ref)}
                                        onPlaceChanged={onToPlaceChanged}
                                        options={{
                                            componentRestrictions: { country: 'gb' },
                                            fields: ['address_components', 'geometry', 'formatted_address', 'name'],
                                            types: ['establishment', 'geocode']
                                        }}
                                    >
                                        <input
                                            type="text"
                                            placeholder={toPlaceholder}
                                            value={toAddress}
                                            onChange={(e) => setToAddress(e.target.value)}
                                            className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-900 outline-none focus:ring-2 ${colours.ring} ${colours.border} transition-all placeholder:text-slate-400 dark:text-white`}
                                        />
                                    </Autocomplete>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── Route Info Strip ── */}
            {(fetching || routeInfo || fetchError) && (
                <div className={`px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-4 text-xs font-semibold ${fetchError ? 'bg-rose-50 dark:bg-rose-900/10' : 'bg-slate-50 dark:bg-slate-800/50'}`}>
                    {fetching && (
                        <span className="flex items-center gap-2 text-slate-500">
                            <span className="w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin inline-block" />
                            Calculating route…
                        </span>
                    )}
                    {!fetching && routeInfo && (
                        <>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${colours.badge}`}>
                                📏 {routeInfo.distance}
                            </span>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${colours.badge}`}>
                                ⏱ {routeInfo.duration}
                            </span>
                            <span className="text-slate-400 ml-auto">Route highlighted on map</span>
                        </>
                    )}
                    {!fetching && fetchError && (
                        <span className="text-rose-600">{fetchError}</span>
                    )}
                </div>
            )}

            {/* ── Map ── */}
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={zoomLevel}
                onLoad={onMapLoad}
                options={mapOptions}
            >
                {/* Directions Route Polyline */}
                {directionsRendererOptions && (
                    <DirectionsRenderer
                        options={directionsRendererOptions}
                    />
                )}

                {/* Custom Marker A (Origin) — hidden in singleSelectTo mode */}
                {fromCoords && !singleSelectTo && window.google && (
                    <Marker
                        position={fromCoords}
                        label={{ text: 'A', color: '#fff', fontWeight: 'bold', fontSize: '12px' }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 12,
                            fillColor: colours.markerFrom,
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2.5,
                        }}
                    />
                )}

                {/* Custom Marker B (Destination) — shown in singleSelectTo mode or normal dual mode */}
                {toCoords && (!singleSelect || singleSelectTo) && window.google && (
                    <Marker
                        position={toCoords}
                        label={{ text: 'B', color: '#fff', fontWeight: 'bold', fontSize: '12px' }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 12,
                            fillColor: colours.markerTo,
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2.5,
                        }}
                    />
                )}
            </GoogleMap>
        </div>
    );
});

RouteMapPicker.displayName = 'RouteMapPicker';

export default RouteMapPicker;
